import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
}

export class ChatErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): State {
    
    // Generate unique error ID for tracking
    const errorId = `chat_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // Track error for analytics
    try {
      if ((window as any).gtag) {
        (window as any).gtag('event', 'exception', {
          description: `Chat Error: ${error.message}`,
          fatal: false,
        });
      }
    } catch (trackingError) {
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorId: '',
    });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleRefreshPage = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="text-lg">Chat Error</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground text-sm">
                Something went wrong while loading the chat. This might be due to:
              </p>
              <ul className="text-left text-sm text-muted-foreground space-y-1">
                <li>• Network connectivity issues</li>
                <li>• Invalid or corrupted chat data</li>
                <li>• Temporary server problems</li>
                <li>• Browser cache issues</li>
              </ul>
              
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-xs font-mono text-muted-foreground">
                  Error ID: {this.state.errorId}
                </p>
                {this.state.error && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {this.state.error.message}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Button 
                  onClick={this.handleRetry}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={this.handleRefreshPage}
                    className="flex-1"
                  >
                    Refresh Page
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={this.handleGoHome}
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
} 